/** @format */

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import axios from "axios";
import { useNavigate } from "react-router-dom";

/**
 * Payroll Setup Wizard
 * Created: October 30, 2025
 * Purpose: Guide users through initial payroll system setup
 */
const PayrollSetupWizard = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [setupStatus, setSetupStatus] = useState({
    payrollConfig: false,
    payHeads: false,
    payTemplates: false,
    employeeAssignments: false,
    firstPayslip: false,
  });
  const [loading, setLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [currentGuide, setCurrentGuide] = useState(null);

  const steps = [
    {
      label: "Payroll Configuration",
      description: "Set up basic payroll settings",
      key: "payrollConfig",
    },
    {
      label: "Create Pay Heads",
      description: "Define salary components (Basic, HRA, PF, etc.)",
      key: "payHeads",
    },
    {
      label: "Create Pay Templates",
      description: "Build salary structures for different roles",
      key: "payTemplates",
    },
    {
      label: "Assign Templates to Employees",
      description: "Link employees to their pay templates",
      key: "employeeAssignments",
    },
    {
      label: "Generate First Payslip",
      description: "Test the system with a sample payslip",
      key: "firstPayslip",
    },
  ];

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      setLoading(true);
      
      // Check payroll config
      const configRes = await axios.get("/payroll/payrollConfig").catch(() => ({ data: null }));
      const hasConfig = configRes.data && configRes.data.length > 0;

      // Check pay heads - API doesn't exist yet, default to false
      const hasPayHeads = false;

      // Check pay templates - API doesn't exist yet, default to false
      const hasTemplates = false;

      // Check employee assignments
      const employeesRes = await axios.get("/employee/getAllEmployees").catch(() => ({ data: [] }));
      const employees = employeesRes.data || [];
      const hasAssignments = employees.some(emp => emp.payTemplateId);

      // Check payslips - API doesn't exist yet, default to false
      const hasPayslips = false;

      setSetupStatus({
        payrollConfig: hasConfig,
        payHeads: hasPayHeads,
        payTemplates: hasTemplates,
        employeeAssignments: hasAssignments,
        firstPayslip: hasPayslips,
      });

      // Set active step to first incomplete step
      if (!hasConfig) setActiveStep(0);
      else if (!hasPayHeads) setActiveStep(1);
      else if (!hasTemplates) setActiveStep(2);
      else if (!hasAssignments) setActiveStep(3);
      else if (!hasPayslips) setActiveStep(4);
      else setActiveStep(5); // All complete

    } catch (err) {
      console.error("Error checking setup status:", err);
    } finally {
      setLoading(false);
    }
  };

  const getCompletionPercentage = () => {
    const completed = Object.values(setupStatus).filter(Boolean).length;
    return (completed / 5) * 100;
  };

  const handleShowGuide = (stepKey) => {
    setCurrentGuide(guides[stepKey]);
    setShowGuide(true);
  };

  const guides = {
    payrollConfig: {
      title: "Payroll Configuration Guide",
      sections: [
        {
          title: "What is Payroll Configuration?",
          content: "Payroll configuration defines the basic settings for your payroll system, including default values, calculation methods, and compliance settings.",
        },
        {
          title: "Steps to Configure",
          content: `
1. Go to Payroll → Configuration
2. Set the following:
   • Payroll Period (Monthly/Yearly)
   • Default Working Days per Month (e.g., 30)
   • Overtime Calculation Method
   • Leave Encashment Rules
   • PF/ESI Settings (if applicable)
3. Click "Save Configuration"
          `,
        },
        {
          title: "Important Settings",
          content: `
• Working Days: Usually 26-30 days per month
• Overtime Rate: Typically 1.5x or 2x of hourly rate
• PF Contribution: 12% (employee) + 12% (employer)
• ESI: 0.75% (employee) + 3.25% (employer)
          `,
        },
      ],
    },
    payHeads: {
      title: "Pay Heads Setup Guide",
      sections: [
        {
          title: "What are Pay Heads?",
          content: "Pay heads are the individual components of salary like Basic Salary, HRA, Conveyance, PF, etc. They can be earnings (additions) or deductions (subtractions).",
        },
        {
          title: "Common Pay Heads to Create",
          content: `
EARNINGS:
• Basic Salary (60% of CTC)
• House Rent Allowance (HRA) - 30% of Basic
• Conveyance Allowance - Fixed amount
• Medical Allowance - Fixed amount
• Special Allowance - Remaining amount

DEDUCTIONS:
• Provident Fund (PF) - 12% of Basic
• Professional Tax - Fixed amount
• Income Tax (TDS) - As per slab
• Loan EMI - If applicable
          `,
        },
        {
          title: "How to Create Pay Heads",
          content: `
1. Go to Payroll → Pay Heads
2. Click "Add Pay Head"
3. Fill in:
   • Name (e.g., "Basic Salary")
   • Category (BASIC/ALLOWANCE/DEDUCTION)
   • Calculation Type:
     - CTCPERCENT: % of total CTC
     - BASICPERCENT: % of basic salary
     - FIXEDAMOUNT: Fixed amount
   • Amount/Percentage
4. Click "Save"
5. Repeat for all pay heads
          `,
        },
        {
          title: "Example: Creating Basic Salary",
          content: `
Name: Basic Salary
Category: BASIC
Calculation Type: CTCPERCENT
Amount/Percentage: 60
(This means Basic = 60% of CTC)
          `,
        },
      ],
    },
    payTemplates: {
      title: "Pay Templates Setup Guide",
      sections: [
        {
          title: "What are Pay Templates?",
          content: "Pay templates are pre-defined salary structures that combine multiple pay heads. They make it easy to assign consistent salary packages to employees in similar roles.",
        },
        {
          title: "Example Templates",
          content: `
JUNIOR TEMPLATE (₹30,000/month):
• Basic: ₹18,000 (60%)
• HRA: ₹5,400 (30% of Basic)
• Conveyance: ₹2,000
• Medical: ₹1,500
• Special: ₹3,100
• PF Deduction: -₹2,160 (12% of Basic)
• Net: ₹27,840

MANAGER TEMPLATE (₹60,000/month):
• Basic: ₹36,000 (60%)
• HRA: ₹10,800 (30% of Basic)
• Conveyance: ₹3,000
• Medical: ₹2,500
• Special: ₹7,700
• PF Deduction: -₹4,320 (12% of Basic)
• Net: ₹55,680
          `,
        },
        {
          title: "How to Create Templates",
          content: `
1. Go to Payroll → Pay Templates
2. Click "Add Template"
3. Fill in:
   • Template Name (e.g., "Junior Employee")
   • Period (MONTHLY/YEARLY)
   • Total CTC (e.g., 30000)
4. Select Basic Pay Head
5. Add Allowances (click "Add Allowance")
   • Select pay head (e.g., HRA)
   • It will auto-calculate based on pay head settings
6. Add Deductions (click "Add Deduction")
   • Select pay head (e.g., PF)
7. Review total and click "Save"
          `,
        },
        {
          title: "Best Practices",
          content: `
• Create templates for each job level (Junior, Senior, Manager, etc.)
• Keep templates simple and standardized
• Use percentage-based calculations for flexibility
• Test templates before assigning to employees
• Document what each template is for
          `,
        },
      ],
    },
    employeeAssignments: {
      title: "Employee Assignment Guide",
      sections: [
        {
          title: "Why Assign Templates?",
          content: "Employees must have a pay template assigned before you can generate their payslips. This links their salary structure to the payroll system.",
        },
        {
          title: "How to Assign Templates",
          content: `
METHOD 1: Individual Assignment
1. Go to Staff → Manage Employee
2. Click on an employee
3. Go to "Pay Template" tab
4. Select template from dropdown
5. Set effective date (when it starts)
6. Enter reason (e.g., "New Hire", "Promotion")
7. Click "Assign Template"

METHOD 2: Bulk Assignment (Coming Soon)
• Assign same template to multiple employees at once
          `,
        },
        {
          title: "Assignment History",
          content: "Every template assignment is tracked with history showing:\n• When it was assigned\n• Who assigned it\n• Previous template (if any)\n• Reason for change\n\nThis creates a complete audit trail of salary changes.",
        },
        {
          title: "What to Assign",
          content: `
Match employees to templates based on:
• Job Level (Junior → Junior Template)
• Department (Sales → Sales Template)
• Experience (1-3 years → Junior, 3-5 → Senior)
• Location (Different cities may have different allowances)
          `,
        },
      ],
    },
    firstPayslip: {
      title: "First Payslip Generation Guide",
      sections: [
        {
          title: "Ready to Generate!",
          content: "Once employees have templates assigned, you can generate their payslips. The system will automatically calculate salaries based on attendance and leave records.",
        },
        {
          title: "Individual Payslip",
          content: `
1. Go to Payroll → Compute Payslip
2. Select employee
3. Set date range (e.g., Nov 1-30, 2025)
4. Leave work days empty (auto-calculates from attendance)
   OR enter manually if needed
5. Click "Compute Payslip"
6. Review the calculation
7. Click "Save" to finalize
          `,
        },
        {
          title: "Batch Payslip (Multiple Employees)",
          content: `
1. Go to Payroll → Batch Payslip
2. Click "Create Batch"
3. Fill in:
   • Batch Name (e.g., "November 2025 - All Staff")
   • From Date / To Date
   • Department (optional - select specific dept)
   • Branch (optional)
4. Click "Save Batch"
5. Go to Payroll → Compute Batch
6. Select the batch you created
7. Click "Compute Payslips"
8. Wait for processing (shows progress bar)
9. Review results
          `,
        },
        {
          title: "What Gets Calculated",
          content: `
The system automatically:
✓ Gets employee's pay template
✓ Fetches attendance records
✓ Calculates work days (paid/unpaid)
✓ Computes pro-rata salary for unpaid days
✓ Adds overtime (if any)
✓ Deducts loan EMI (if any)
✓ Applies all allowances and deductions
✓ Generates final net salary
          `,
        },
        {
          title: "After Generation",
          content: `
• View all payslips in Payroll → Payslip List
• Print individual payslips
• Export to Excel (coming soon)
• Email to employees (coming soon)
• Post to accounting system (Week 4 feature)
          `,
        },
      ],
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Payroll Setup Wizard
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Welcome! Let's set up your payroll system step by step.
      </Typography>

      {/* Progress Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Setup Progress</Typography>
            <Chip
              label={`${Math.round(getCompletionPercentage())}% Complete`}
              color={getCompletionPercentage() === 100 ? "success" : "primary"}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={getCompletionPercentage()}
            sx={{ height: 10, borderRadius: 5 }}
          />
          <Box mt={2}>
            <Grid container spacing={1}>
              {steps.map((step) => (
                <Grid item xs={12} sm={6} md={4} key={step.key}>
                  <Box display="flex" alignItems="center">
                    {setupStatus[step.key] ? (
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                    ) : (
                      <RadioButtonUncheckedIcon color="disabled" sx={{ mr: 1 }} />
                    )}
                    <Typography
                      variant="body2"
                      color={setupStatus[step.key] ? "success.main" : "text.secondary"}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Setup Steps */}
      <Card>
        <CardContent>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.key}>
                <StepLabel
                  optional={
                    setupStatus[step.key] ? (
                      <Chip label="Completed" size="small" color="success" />
                    ) : null
                  }
                >
                  {step.label}
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {step.description}
                  </Typography>

                  {/* Step-specific content */}
                  {step.key === "payrollConfig" && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Set up basic payroll settings like working days, overtime rules, and
                      compliance settings (PF, ESI).
                    </Alert>
                  )}

                  {step.key === "payHeads" && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Create salary components like Basic Salary (60% of CTC), HRA (30% of
                      Basic), Conveyance, PF (12% deduction), etc.
                    </Alert>
                  )}

                  {step.key === "payTemplates" && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Combine pay heads into templates. Example: "Junior Employee Template"
                      with ₹30,000 CTC including Basic, HRA, Allowances, and PF deduction.
                    </Alert>
                  )}

                  {step.key === "employeeAssignments" && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Assign pay templates to employees. Go to each employee's profile →
                      Pay Template tab → Select template → Assign.
                    </Alert>
                  )}

                  {step.key === "firstPayslip" && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      You're ready! Generate your first payslip to test the system. The
                      system will auto-calculate salary based on attendance.
                    </Alert>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<InfoIcon />}
                      onClick={() => handleShowGuide(step.key)}
                      sx={{ mr: 1 }}
                    >
                      View Detailed Guide
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => {
                        const routes = {
                          payrollConfig: "/userdashboard/payroll/configuration/PayrollSettings",
                          payHeads: "/userdashboard/payroll/configuration/PayrollPayHead",
                          payTemplates: "/userdashboard/payroll/configuration/PayTemplatePayroll",
                          employeeAssignments: "/userdashboard/staff/hrm/manageEmployee",
                          firstPayslip: "/userdashboard/payroll/paySlip/create",
                        };
                        navigate(routes[step.key] || "/userdashboard/payroll/dashboard");
                      }}
                    >
                      {setupStatus[step.key] ? "Review" : "Start Setup"}
                    </Button>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="text"
                      onClick={() => setActiveStep(index + 1)}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      {index === steps.length - 1 ? "Finish" : "Next"}
                    </Button>
                    <Button
                      disabled={index === 0}
                      onClick={() => setActiveStep(index - 1)}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {activeStep === steps.length && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Setup Complete! 🎉
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Your payroll system is fully configured and ready to use.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/userdashboard/payroll/dashboard")}
              >
                Go to Payroll Dashboard
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Guide Dialog */}
      <Dialog
        open={showGuide}
        onClose={() => setShowGuide(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{currentGuide?.title}</DialogTitle>
        <DialogContent>
          {currentGuide?.sections.map((section, index) => (
            <Accordion key={index} defaultExpanded={index === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{section.title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}
                >
                  {section.content}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGuide(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollSetupWizard;
